import {
    Alert,
    Autocomplete,
    Box,
    Checkbox,
    Chip, FormControl,
    Grid, InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography
} from "@mui/material";
import CircleIcon from "@mui/icons-material/Circle";
import React, {useEffect, useMemo, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {
    RepeatRule,
    Reservation, ReservationStatus, SlotType, TableReservationEvent
} from "crqlar-react-common/dist/models/reservation/reservation.model";
import {Resource} from "crqlar-react-common/dist/models/resource/resource.model";
import CDatePicker from "crqlar-react-common/dist/components/CDatePicker";
import {
    useAllChildResources,
    useResourcesWithAvailability,
    useAllRootResourcesForSector
} from "crqlar-react-common/dist/utils/hooks/resources/useAllRootResources";
import {AgeCategory} from "crqlar-react-common/dist/models/ageCategory/ageCategory.model";
import ReservationDetailsWrapper from "../ReservationDetailsWrapper";
import {useService, useServicesForSector} from "crqlar-react-common/dist/utils/hooks/services/useServices";
import {Service} from "crqlar-react-common/dist/models/service/service.model";
import {useCurrentApp, useCurrentAppId} from "crqlar-react-common/dist/utils/hooks/app/useOrganizationApps";
import {useSector} from "crqlar-react-common/dist/utils/hooks/sector/useSectors";
import {
    addMinutes, createReservationStartDateBasedOnPeriod,
    formatDateDMYHM,
    formatDateTime,
    isAfter,
    isBefore,
    startOfDay
} from "crqlar-react-common/dist/utils/date.util";
import {ReservationGroup} from "crqlar-react-common/dist/models/reservation/reservationGroup.model";
import {createMultidayReservations} from "crqlar-react-common/dist/utils/reservation.util";
import {ResourceAvailabilityDTO} from "crqlar-react-common/dist/models/resource/resourceAvailability.model";
import {CommonThemeColors} from "crqlar-react-common/dist/components/theme";
import {getSlotTypeBackgroundColor, getSlotTypeColor} from "crqlar-react-common/dist/utils/color.util";
import {getSlotTypeIcon} from "crqlar-react-common/dist/utils/icon.util";
import {useReservationsState} from "../../../../store/reservations.reducer";
import {isResourceAvailable} from "crqlar-react-common/dist/utils/resource.util";
import {ServiceType} from "crqlar-react-common/dist/models/service/serviceListRequest.dto";
import {Rate} from "crqlar-react-common/dist/models/rate/rate.model";
import {MbscDatepickerPageLoadedEvent} from "@mobiscroll/react";
import {DatepickerBase} from "@mobiscroll/react/dist/src/core/components/datepicker/datepicker";
import {
    checkBreakfastOnCheckoutDate,
    checkDinnerOnCheckInDate,
    cutDinnerFromCheckoutDate
} from "../multiday/multiday.util";
import {useReadOnly} from "crqlar-react-common/dist/utils/hooks/users/useReadOnly";
import {read} from "fs";
import {
    autoAssignResourcesForService,
    releaseAutoAssignedResources
} from "../../../../utils/serviceResourceAutoAssign.util";

interface Props {
    reservation?: Reservation
    setReservation: (reservation?: Reservation) => void
    // availabilityForActiveReservation?: RestaurantAvailableTablesResponse
    // loadingAvailabilities: boolean,
    onSave?: (
        allowNotify?: boolean,
        updateOption?: 'single' | 'linkedReservations' | 'subsequentLinkedReservations',
        changes?: {
            field: string
            oldValue: any
            newValue: any
        }[],
        eatPeriod?: 'breakfast' | 'lunch' | 'dinner' | 'all' | undefined
    ) => void,
    onResend?: () => void,
    ageCategories?: AgeCategory[]
    linkedReservations?: Reservation[]
    setLinkedReservations: (reservations: Reservation[]) => void
    reservationGroup?: ReservationGroup
    setReservationGroup: (group?: ReservationGroup) => void
    onClose: () => void,
    onShowFolios?: () => void
    onShowLogs?: () => void
    serviceInfo?: Service
    onOpenCommunication?: () => void
    onOpenFormSelector?: () => void
    originalReservation?: Reservation,
    rates?: Rate[] | undefined
    defaultMultidayUntil?: Date
    defaultAllowNotify?: boolean
    selectedSlotType?: SlotType
    setSelectedSlotType?: (slotType: SlotType) => void
    onSaveBlock?: (name: string | undefined) => void
    loading?: boolean
    onSaveAndSendNoShowLink?: () => void
    availabilityWarning?: React.ReactNode
    /**
     * Stay window of the guest the dialog was last recalculated for. Guests of one reservation
     * group can depart on different days, so these win over the group-wide dates the floorplan
     * halfboard flow parks in redux.
     */
    stayCheckInDate?: Date
    stayCheckOutDate?: Date
}

const ReservationDetailsRestaurant = ({
                                          reservation,
                                          setReservation,
                                          // availabilityForActiveReservation,
                                          // loadingAvailabilities,
                                          onSave,
                                          onResend,
                                          ageCategories,
                                          linkedReservations,
                                          setLinkedReservations,
                                          onClose,
                                          onShowFolios,
                                          onShowLogs,
                                          serviceInfo,
                                          onOpenCommunication,
                                          onOpenFormSelector,
                                          originalReservation,
                                        rates,
                                        defaultMultidayUntil,
                                        defaultAllowNotify,
                                        selectedSlotType,
                                        setSelectedSlotType,
                                        onSaveBlock,
                                        loading,
                                        onSaveAndSendNoShowLink,
                                        availabilityWarning,
                                        stayCheckInDate,
                                        stayCheckOutDate
                                      }: Props) => {

    const appId = useCurrentAppId()
    const appData = useCurrentApp()
    const sector = useSector(reservation?.sectorId ?? appData?.configuration?.sectorId)
    const {t} = useTranslation()
    const [canEdit, setCanEdit] = useState(true)
    const resources = useAllRootResourcesForSector(sector?.id)
    const restaurantId = reservation?.resourceIds?.find(r => resources?.find(res => res?.id === r))
    const tables = useAllChildResources(resources?.map(r => r.id!).filter(Boolean) || [])

    const readOnlyUser = useReadOnly()

    const defaultService = useService(sector?.defaultServiceId)

    // Ref to capture the original "other" templates (one per eatPeriod) before multiday expansion.
    // This prevents the endless reservations bug where re-firing the multiday effect
    // would multiply already-expanded copies exponentially.
    const otherTemplatesRef = useRef<Reservation[]>([])

    useEffect(() => {
        if (!linkedReservations || linkedReservations.length === 0) {
            otherTemplatesRef.current = []
            return
        }

        if (otherTemplatesRef.current.length === 0) {
            const eatPeriods = new Set(linkedReservations.map(r => r.meta?.eatPeriod).filter(Boolean))
            if (eatPeriods.size > 1) {
                // Use the first linked reservation's period as the base (it matches the draft).
                // We cannot rely on reservation?.meta?.eatPeriod here because on the first
                // render cycle the parent's tmpReservation may still be undefined.
                const basePeriod = linkedReservations[0]?.meta?.eatPeriod
                otherTemplatesRef.current = linkedReservations.filter(
                    r => r.meta?.eatPeriod !== basePeriod
                )
            }
        }
    }, [linkedReservations])

    // When switching to BLOCK mode for a new slot, auto-select the first restaurant so
    // entityId is always populated for AvailabilityService.createAvailability.
    useEffect(() => {
        if (
            selectedSlotType === SlotType.BLOCK &&
            !reservation?.id &&
            !restaurantId &&
            resources?.[0]?.id
        ) {
            setReservation({ ...reservation, resourceIds: [resources[0].id!] })
        }
    }, [selectedSlotType, restaurantId, resources])

    const {
        checkInDate: groupCheckInDate,
        checkOutDate: groupCheckOutDate
    } = useReservationsState(appId)

    // Redux carries the stay of the reservation group the halfboard drag started from. As soon as
    // a guest is picked in the dialog, that guest's own stay takes over - rooms 22/24 of a group
    // can check out a day earlier than rooms 61/84. The redux values are re-wrapped because the
    // sector state is normalised from plain values and can hand back a date-like string.
    const checkInDate = useMemo(
        () => stayCheckInDate ?? (groupCheckInDate ? new Date(groupCheckInDate) : undefined),
        [stayCheckInDate, groupCheckInDate]
    )
    const checkOutDate = useMemo(
        () => stayCheckOutDate ?? (groupCheckOutDate ? new Date(groupCheckOutDate) : undefined),
        [stayCheckOutDate, groupCheckOutDate]
    )

    const tablesWithAvailability =
        useResourcesWithAvailability({
            expectedStartDate: reservation?.expectedStartDate,
            expectedEndDate: reservation?.expectedEndDate,
            parentId: restaurantId
        })

    const services = useServicesForSector(sector?.id,  ServiceType.REGULAR)

    // Tables the service requirements picked for the user - released again when
    // another service is chosen, so only the manual picks survive a service switch
    const autoAssignedResourceIdsRef = useRef<string[]>([])

    useEffect(() => {
        if (reservation?.serviceId || !restaurantId || !services.length) return

        const firstAssociatedService = services.find((service) =>
            service.resourceIds?.includes(restaurantId)
        )

        if (firstAssociatedService) {
            setReservation({
                ...reservation,
                serviceId: firstAssociatedService.id,
                // Keep the time frame chosen by the user (e.g. calendar drag-to-create);
                // only derive the end date from the service duration when none was set
                expectedEndDate: reservation?.expectedEndDate
                    ?? addMinutes(reservation?.expectedStartDate, firstAssociatedService.durationMinutes || 60)
            })
        }
    }, [services, restaurantId, reservation?.serviceId])

    const [tmpMultidayUntil, setTmpMultidayUntil] = useState<Date | undefined>(defaultMultidayUntil)

    // Triggering only the initial navigation to check-in date
    const [hasInitiallyNavigated, setHasInitiallyNavigated] = useState(false)

    // Picking another guest hands down that guest's own check-out date. Follow it on new drafts
    // so the End Date - and with it the whole serial series - ends on the day THAT guest leaves.
    // Existing reservations keep the end date they were saved with.
    useEffect(() => {
        if (reservation?.id) return
        if (!defaultMultidayUntil) return
        if (tmpMultidayUntil?.getTime() === defaultMultidayUntil.getTime()) return

        setTmpMultidayUntil(defaultMultidayUntil)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultMultidayUntil?.getTime(), reservation?.id])

    // Re-centre the End Date picker on the newly picked guest's check-in
    useEffect(() => {
        setHasInitiallyNavigated(false)
    }, [checkInDate?.getTime()])

    useEffect(() => {
        if (reservation?.id) return

        if (tmpMultidayUntil) {
            const multidayOfBaseReservation = createMultidayReservations(reservation!, tmpMultidayUntil, RepeatRule.EVERY_DAY, [])

            // Clone templates from ref and apply current reservation fields (no mutation)
            const templates = otherTemplatesRef.current.map(t => ({
                ...t,
                resourceIds: reservation?.resourceIds,
                serviceId: reservation?.serviceId,
                guestCounts: reservation?.guestCounts,
                status: reservation?.status,
                notes: reservation?.notes,
            }))

            if (templates.length === 0) {
                setLinkedReservations(
                    trimReservationsToStay(multidayOfBaseReservation)
                )
                return
            }

            const multidayOfOtherLinkedReservations = templates.flatMap(r => {
                return createMultidayReservations(r, tmpMultidayUntil!, RepeatRule.EVERY_DAY, [])
            })

            setLinkedReservations(
                trimReservationsToStay([...multidayOfBaseReservation, ...multidayOfOtherLinkedReservations])
            )
        } else {
            setLinkedReservations([])
        }
    }, [reservation, tmpMultidayUntil]);

    // Remove reservations that fall outside the guest's actual stay window:
    // - On arrival day: skip meals that start before check-in (e.g. check-in 14:00 → no breakfast 07:00, no lunch 12:00)
    // - On departure day: skip meals that start at or after check-out (e.g. check-out 05:00 → no breakfast 07:00; check-out 14:00 → no dinner 18:00)
    const trimReservationsToStay = (reservations: Reservation[]) => {
        let result = [...reservations]

        // Redux checkInDate/checkOutDate are only populated by the floorplan halfboard flow.
        // Other entry points (e.g. the restaurant arrivals list) open this dialog with just the
        // End Date field (tmpMultidayUntil) set, so fall back to the multiday series bounds -
        // the draft's start day for check-in and the End Date for check-out. Without this the
        // departure-day guards no-op and a dinner ends up on the checkout date.
        const effectiveCheckIn = checkInDate ?? reservation?.expectedStartDate
        const effectiveCheckOut = checkOutDate ?? tmpMultidayUntil

        // Remove meals on the arrival day that start before check-in
        if (checkInDate) {
            const checkInTime = new Date(checkInDate)
            const arrivalDay = new Date(checkInTime)
            arrivalDay.setHours(0, 0, 0, 0)

            result = result.filter(r => {
                if (!r.expectedStartDate) return true
                const resDay = new Date(r.expectedStartDate)
                resDay.setHours(0, 0, 0, 0)
                // Only filter reservations on the arrival day
                if (resDay.getTime() !== arrivalDay.getTime()) return true
                // Keep if the meal starts at or after check-in
                return !isBefore(r.expectedStartDate, checkInTime)
            })
        }

        // Remove meals on the departure day that start at or after check-out
        // (e.g. checkout at 05:00 removes breakfast at 07:00, checkout at 14:00 removes dinner at 18:00)
        if (checkOutDate) {
            const checkOutTime = new Date(checkOutDate)
            const departureDay = new Date(checkOutTime)
            departureDay.setHours(0, 0, 0, 0)

            result = result.filter(r => {
                if (!r.expectedStartDate) return true
                const resDay = new Date(r.expectedStartDate)
                resDay.setHours(0, 0, 0, 0)
                // Only filter reservations on the departure day
                if (resDay.getTime() !== departureDay.getTime()) return true
                // Keep if the meal starts before check-out
                return isBefore(r.expectedStartDate, checkOutTime)
            })
        }

        // EDGE CASE - Make sure that there is breakfast added on checkout date
        // Guests must have breakfast on check out date - no matter what!
        if(localStorage.getItem('selectedEatPeriod')?.includes('breakfast')) {
            // Only if breakfast is selected
            result = checkBreakfastOnCheckoutDate(result, defaultService, sector, effectiveCheckOut as Date)
        }

        // EDGE CASE - Make sure that there is dinner added on checkin date
        // Guests must have dinner on check in date - no matter what!
        if(localStorage.getItem('selectedEatPeriod')?.includes('dinner')) {
            // Only if dinner is selected
            result = checkDinnerOnCheckInDate(result, defaultService, sector, effectiveCheckIn as Date)
        }

        // EDGE CASE - Cut the dinner from the checkout (departure) date
        result = cutDinnerFromCheckoutDate(result, effectiveCheckOut)

        return result
    }

    const renderRestaurant = () => {

        return (
            <FormControl fullWidth variant="outlined" size="small">
                <InputLabel id="restaurant-label">{t('Restaurant')}</InputLabel>
                <Select
                    labelId="restaurant-label"
                    label={t('Restaurant')}
                    sx={{width: '100%'}}
                    variant="outlined"
                    value={restaurantId || ''}
                    onChange={e => {
                        const newRestaurantId = e.target.value as string

                        // Tables belong to their parent restaurant. Clear every
                        // previous table selection so the reservation cannot retain
                        // tables from the restaurant that was just replaced.
                        setReservation({
                            ...reservation,
                            resourceIds: [newRestaurantId]
                        })
                    }}
                    disabled={!canEdit || readOnlyUser}
                >
                    {resources?.map(r => (
                        <MenuItem value={r.id} key={r.id}>
                            <Stack direction="row" gap={1} alignItems="center">
                                <CircleIcon sx={{ color: r?.meta?.tagColor || 'black' }} />
                                <Typography>{r?.name || t('Show all')}</Typography>
                            </Stack>
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>



        )
    }




    const renderTableAlert = () => {
        const explicitCount = (reservation?.guestCounts ?? []).reduce(
            (sum, gc) => sum + (gc.count ?? 0),
            0
        );
        const selectedTableIds =
            (reservation?.resourceIds ?? []).filter((id) => id !== restaurantId);
        const selectedCapacity = selectedTableIds.reduce((sum, id) => {
            const cap = tables.find(t => t.id === id)?.capacity ?? 0;
            return sum + Number(cap);
        }, 0);
        const missingSeats = Math.max(0, explicitCount - selectedCapacity);

        if (missingSeats <= 0) return null;

        return (
            <Alert severity="warning" variant="outlined">
                {t("Selected tables provide", {
                    cap: selectedCapacity,
                    missing: missingSeats,
                    guests: explicitCount,
                })}
            </Alert>
        );
    }

    const renderMultipleRestaurantsAlert = () => {
        const selectedRestaurantIds = new Set(
            (reservation?.resourceIds ?? [])
                .map(id => tables.find(table => table.id === id)?.parentId)
                .filter((parentId): parentId is string => Boolean(parentId))
        );

        if (selectedRestaurantIds.size <= 1) return null;

        return (
            <Alert severity="warning" variant="outlined">
                {t("Selected tables belong to multiple restaurants.")}
            </Alert>
        );
    }

    const renderTables = () => {

        const renderTableNameWithCapacity = (table: Resource) => {
            const name = table.name || "-";
            const capacityText = table.capacity ? ` (${table.capacity})` : "";
            // Remove 'table' prefix - product decision
            return `${name}${capacityText}`;
        };

        const selectedTableIds =
            (reservation?.resourceIds ?? []).filter((id) => id !== restaurantId);

        // Sort so the selected restaurant's tables form the first group, then
        // the remaining restaurants by name. groupBy needs contiguous groups,
        // and within each restaurant available tables come first.
        const sortedOptions = [...tables].sort((a, b) => {
            const aSelected = a.parentId === restaurantId
            const bSelected = b.parentId === restaurantId
            if (aSelected !== bSelected) return aSelected ? -1 : 1

            const aRestName = resources.find(r => r.id === a.parentId)?.name || ''
            const bRestName = resources.find(r => r.id === b.parentId)?.name || ''
            const cmp = aRestName.localeCompare(bRestName)
            if (cmp !== 0) return cmp
            const aAvail = isResourceAvailable(a, tablesWithAvailability, reservation)
            const bAvail = isResourceAvailable(b, tablesWithAvailability, reservation)
            if (aAvail && !bAvail) return -1
            if (!aAvail && bAvail) return 1
            return 0
        })

        return (
              <Autocomplete
                  id={'categories'}
                  multiple
                  disableCloseOnSelect
                  options={sortedOptions}
                  groupBy={(option) => option.parentId || ''}
                  renderGroup={(params) => {
                      const restaurant = resources.find(r => r.id === params.group)
                      const groupTableIds = tables
                          .filter(t => t.parentId === params.group)
                          .map(t => t.id!)
                      const allSelected =
                          groupTableIds.length > 0 &&
                          groupTableIds.every(id => selectedTableIds.includes(id))
                      const someSelected = groupTableIds.some(id => selectedTableIds.includes(id))

                      const handleToggleAll = (e: React.MouseEvent) => {
                          e.stopPropagation()
                          const newTableIds = allSelected
                              ? selectedTableIds.filter(id => !groupTableIds.includes(id))
                              : Array.from(new Set([...selectedTableIds, ...groupTableIds]))
                          const newResources = restaurantId
                              ? [restaurantId, ...newTableIds]
                              : newTableIds

                          // Anything the user touches becomes his own pick and must not be
                          // released by the next service change
                          autoAssignedResourceIdsRef.current = autoAssignedResourceIdsRef.current
                              .filter(id => newResources.includes(id))

                          setReservation({ ...reservation, resourceIds: newResources })
                      }

                      return (
                          <li key={params.key}>
                              <Box
                                  onMouseDown={e => e.preventDefault()}
                                  onClick={handleToggleAll}
                                  sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      px: 1,
                                      py: 0.5,
                                      bgcolor: 'action.selected',
                                      position: 'sticky',
                                      top: '-8px',
                                      zIndex: 1,
                                      cursor: 'pointer',
                                      userSelect: 'none',
                                  }}
                              >
                                  <Checkbox
                                      size="small"
                                      checked={allSelected}
                                      indeterminate={someSelected && !allSelected}
                                      disableRipple
                                      sx={{ pointerEvents: 'none' }}
                                  />
                                  <Typography variant="body2" fontWeight="bold">
                                      {restaurant?.name || params.group}
                                  </Typography>
                              </Box>
                              <ul style={{ padding: 0 }}>{params.children}</ul>
                          </li>
                      )
                  }}
                  size={'small'}
                  getOptionLabel={(option: Resource) => renderTableNameWithCapacity(option).replace(/,\s*$/, "")}
                  value={reservation?.resourceIds?.filter(r => r !== restaurantId).map(id => tables.find(c => c.id === id)).filter(Boolean) as Resource[] || []}
                  onOpen={() => {
                      setTimeout(() => {
                          const listbox = document.querySelector('#categories-listbox')
                          if (listbox) {
                              listbox.scrollTop = 0
                          }
                      }, 1)
                  }}
                  renderInput={(params) => (
                      <TextField
                          {...params}
                          fullWidth
                          size="small"
                          label={t('Tables')}
                      />
                  )}
                  renderOption={(props, option: Resource) => {
                      // Availability colours are only fetched for the selected restaurant;
                      // tables from other restaurants are shown without a colour tint.
                      const backgroundColor = option.parentId === restaurantId
                          ? (isResourceAvailable(option, tablesWithAvailability, reservation)
                              ? CommonThemeColors.sys.success.light
                              : CommonThemeColors.sys.error.light)
                          : undefined

                      const labelWithoutTrailingComma = renderTableNameWithCapacity(option).replace(/,\s*$/, "")

                      return (
                          <Box component="li" {...props} key={option.id} style={{ display: 'flex', alignItems: 'center' }}>
                              <Chip
                                  label={labelWithoutTrailingComma}
                                  style={{
                                      backgroundColor,
                                      color: 'black',
                                  }}
                              />
                          </Box>
                      )
                  }}
                  fullWidth
                  onChange={(e, value, reason, details) => {
                      if (reason === 'removeOption') {
                          const removedOption = details?.option
                          const tableWithAvailability = tablesWithAvailability.find(t => t.id === removedOption?.id) as ResourceAvailabilityDTO
                          if (tableWithAvailability) {
                              tableWithAvailability.available = true
                          }
                      }
                      const newResources = restaurantId
                          ? [restaurantId, ...value.map((v: any) => v.id)]
                          : value.map((v: any) => v.id)

                      // Anything the user touches becomes his own pick and must not be
                      // released by the next service change
                      autoAssignedResourceIdsRef.current = autoAssignedResourceIdsRef.current
                          .filter(id => newResources.includes(id))

                      setReservation({ ...reservation, resourceIds: newResources })
                  }}
              />
        )
    }

    const renderService = () => {
        return (
            <Autocomplete
                sx={{opacity: 0.6}}
                id={'service'}
                options={services}
                size={'small'}
                getOptionLabel={(option: Service) => option.name as string}
                value={services.find(s => s.id === reservation?.serviceId) || null}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        fullWidth
                        size={'small'}
                        label={t('Service')}
                    />
                )}

                fullWidth
                onChange={(_e, value) => {
                    // Assign the first restaurant linked to the picked service so the
                    // restaurant is auto-selected when a service is chosen.
                    const firstLinkedRestaurantId =
                        value?.resourceIds?.find(id => resources?.some(r => r.id === id))

                    // Free the tables the previous service assigned before filling
                    // the requirements of the newly picked one
                    const keptResourceIds = releaseAutoAssignedResources(
                        reservation?.resourceIds,
                        autoAssignedResourceIdsRef.current
                    )

                    const newResourceIds = firstLinkedRestaurantId
                        ? keptResourceIds.filter(r => r !== restaurantId).concat(firstLinkedRestaurantId)
                        : keptResourceIds

                    // Only the tables of the resulting restaurant may be auto-assigned -
                    // the restaurant itself is already handled by the linked-service rule above
                    const selectedRestaurantId = firstLinkedRestaurantId || restaurantId

                    const {resourceIds, addedResourceIds} = autoAssignResourcesForService({
                        service: value || undefined,
                        resources: tables.filter(table => table.parentId === selectedRestaurantId),
                        selectedResourceIds: newResourceIds,
                        resourcesWithAvailability: tablesWithAvailability
                    })

                    autoAssignedResourceIdsRef.current = addedResourceIds

                    setReservation({
                        ...reservation,
                        serviceId: value?.id,
                        resourceIds,
                        expectedEndDate: addMinutes(reservation?.expectedStartDate, value?.durationMinutes || 60)
                    })
                }}
            />
        )
    }

    const events = Object.values(TableReservationEvent).map(e => e)

    const renderEvent = () => {
        return (
            <Autocomplete
                id={'event'}
                options={events}
                size={'small'}
                // Now showing the label text, instead of capitalized words with underscore
                // It still binds and saves event data IN_THIS_FORMAT
                getOptionLabel={(option: TableReservationEvent) => option ? t(`Restaurant event ${option}`) : t('(Not set)')}
                value={reservation?.tableReservationEvent || null}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        fullWidth
                        size={'small'}
                        label={t('Event')}
                    />
                )}

                fullWidth
                onChange={(e, value) => {
                    setReservation({
                        ...reservation,
                        tableReservationEvent: value as TableReservationEvent
                    })
                }}
            />
        )
    }

    const multidayOptions = [
        null,
        RepeatRule.EVERY_DAY,

        // Hiding others as suggested by product

        // RepeatRule.EVERY_SECOND_DAY,
        // RepeatRule.EVERY_WEEKDAY,
        // RepeatRule.EVERY_WEEKEND,
        // RepeatRule.EVERY_WEEK,
        // RepeatRule.EVERY_2_WEEKS,
        // RepeatRule.EVERY_3_WEEKS,
        // RepeatRule.EVERY_4_WEEKS,
        // RepeatRule.EVERY_5_WEEKS,
        // RepeatRule.EVERY_6_WEEKS,
        // RepeatRule.EVERY_7_WEEKS,
        // RepeatRule.EVERY_8_WEEKS,
        // RepeatRule.EVERY_MONTH,
        // RepeatRule.EVERY_6_MONTHS,
        // RepeatRule.EVERY_YEAR,
        // RepeatRule.EVERY_MONTH_SAME_FIRST_WEEKDAY,
        // RepeatRule.EVERY_MONTH_SAME_LAST_WEEKDAY,
        // RepeatRule.CUSTOM
    ]

    const renderSlotType = () => {
        return (
            <Stack direction={'row'} alignItems={'center'} spacing={2}>
                <ToggleButtonGroup
                    id={'btn-slot-type-restaurant'}
                    value={selectedSlotType || SlotType.RESERVATION}
                    exclusive
                    onChange={(e, value: SlotType) => {
                        if (value) {
                            setSelectedSlotType?.(value)
                        }
                    }}
                    size={'small'}
                >
                    {
                        [SlotType.RESERVATION, SlotType.SERIAL, SlotType.BLOCK].map((type) => {
                            const typeColor = getSlotTypeColor(type)
                            const typeBg = getSlotTypeBackgroundColor(type)
                            return (
                                <ToggleButton
                                    key={type}
                                    value={type}
                                    sx={{
                                        '&.Mui-selected': {
                                            color: typeColor,
                                            backgroundColor: typeBg,
                                            border: `1px solid ${typeColor}`
                                        },
                                        '&.Mui-selected:hover': {
                                            backgroundColor: typeBg
                                        }
                                    }}
                                >
                                    <Stack direction={'row'} alignItems={'center'} spacing={1}>
                                        <Typography variant={'body1'} sx={{whiteSpace: 'nowrap'}}>
                                            {t(`Slot type ${type}`)}
                                        </Typography>
                                        {getSlotTypeIcon(type)}
                                    </Stack>
                                </ToggleButton>
                            )
                        })
                    }
                </ToggleButtonGroup>
            </Stack>
        )
    }

    const renderMultidayOption = (option: RepeatRule | null) => {
        switch (option) {
            //  Hiding others as suggested by product

            // case RepeatRule.EVERY_WEEK:
            //     return t('RepeatRule_EVERY_WEEK', {weekday: formatDateTime('EEEE', reservation?.expectedStartDate!)})
            // case RepeatRule.EVERY_2_WEEKS:
            //     return t('RepeatRule_EVERY_2_WEEKS', {weekday: formatDateTime('EEEE', reservation?.expectedStartDate!)})
            // case RepeatRule.EVERY_3_WEEKS:
            //     return t('RepeatRule_EVERY_3_WEEKS', {weekday: formatDateTime('EEEE', reservation?.expectedStartDate!)})
            // case RepeatRule.EVERY_4_WEEKS:
            //     return t('RepeatRule_EVERY_4_WEEKS', {weekday: formatDateTime('EEEE', reservation?.expectedStartDate!)})
            // case RepeatRule.EVERY_5_WEEKS:
            //     return t('RepeatRule_EVERY_5_WEEKS', {weekday: formatDateTime('EEEE', reservation?.expectedStartDate!)})
            // case RepeatRule.EVERY_6_WEEKS:
            //     return t('RepeatRule_EVERY_6_WEEKS', {weekday: formatDateTime('EEEE', reservation?.expectedStartDate!)})
            // case RepeatRule.EVERY_7_WEEKS:
            //     return t('RepeatRule_EVERY_7_WEEKS', {weekday: formatDateTime('EEEE', reservation?.expectedStartDate!)})
            // case RepeatRule.EVERY_8_WEEKS:
            //     return t('RepeatRule_EVERY_8_WEEKS', {weekday: formatDateTime('EEEE', reservation?.expectedStartDate!)})
            // case RepeatRule.EVERY_MONTH:
            //     return t('RepeatRule_EVERY_MONTH', {weekday: formatDateTime('d', reservation?.expectedStartDate!)})
            // case RepeatRule.EVERY_6_MONTHS:
            //     return t('RepeatRule_EVERY_6_MONTHS', {weekday: formatDateTime('d', reservation?.expectedStartDate!)})
            // case RepeatRule.EVERY_YEAR:
            //     return t('RepeatRule_EVERY_YEAR', {weekday: formatDateTime('d MMMM', reservation?.expectedStartDate!)})
            // case RepeatRule.EVERY_WEEKDAY:
            //     return t('RepeatRule_EVERY_WEEKDAY')
            // case RepeatRule.EVERY_WEEKEND:
            //     return t('RepeatRule_EVERY_WEEKEND')
            // case RepeatRule.EVERY_MONTH_SAME_FIRST_WEEKDAY:
            //     return t('RepeatRule_EVERY_MONTH_SAME_FIRST_WEEKDAY', {weekday: formatDateTime('EEEE', reservation?.expectedStartDate!)})
            // case RepeatRule.EVERY_MONTH_SAME_LAST_WEEKDAY:
            //     return t('RepeatRule_EVERY_MONTH_SAME_LAST_WEEKDAY', {weekday: formatDateTime('EEEE', reservation?.expectedStartDate!)})
            // case RepeatRule.CUSTOM:
            //     return t('RepeatRule_CUSTOM')
            // case RepeatRule.EVERY_SECOND_DAY:
            //     return t('RepeatRule_EVERY_SECOND_DAY')
            case RepeatRule.EVERY_DAY:
                return t('RepeatRule_EVERY_DAY')
            default:
                return 'None'
        }
    }

    const tableAlert = renderTableAlert()
    const multipleRestaurantsAlert = renderMultipleRestaurantsAlert()

    return (
        <ReservationDetailsWrapper
            reservation={reservation}
            setReservation={setReservation}
            onSave={onSave}
            onSaveBlock={onSaveBlock}
            onResend={onResend}
            ageCategories={ageCategories}
            resources={resources}
            availabilityWarning={availabilityWarning}
            restaurantInputSizing
            restaurantEvent={(
                <>
                    {renderEvent()}
                </>
            )}
            slotTypeToggle={(
                <Grid item xs={12} md={12} sx={{alignSelf: 'center'}}>
                    {renderSlotType()}
                </Grid>
            )}
            // Putting the service dropdown out of accordion for restaurant
            position1Content={(
                <Grid item xs={12} md={12} sx={{alignSelf: 'center'}}>
                    {renderService()}
                </Grid>
            )}
            position2Content={tableAlert || multipleRestaurantsAlert ? (
                <>
                    {multipleRestaurantsAlert && (
                        <Grid item xs={12}>
                            {multipleRestaurantsAlert}
                        </Grid>
                    )}
                    {tableAlert && (
                        <Grid item xs={12}>
                            {tableAlert}
                        </Grid>
                    )}
                </>
            ) : undefined}
            restaurantResource={
                <Grid item xs={12} md={6} sx={{alignSelf: 'center'}}>
                    {renderRestaurant()}
                </Grid>
            }
            tableResources={
                <Grid item xs={12} md={6} sx={{alignSelf: 'center'}}>
                    {renderTables()}
                </Grid>
            }
            onClose={onClose}
            onShowFolios={onShowFolios}
            onShowLogs={onShowLogs}
            serviceInfo={serviceInfo}
            onOpenCommunication={onOpenCommunication}
            onOpenFormSelector={onOpenFormSelector}
            originalReservation={originalReservation}
            linkedReservations={linkedReservations}
            defaultAllowNotify={defaultAllowNotify}
            selectedSlotType={selectedSlotType}
            loading={loading}
            onSaveAndSendNoShowLink={onSaveAndSendNoShowLink}
        >
            <>
                {
                    selectedSlotType === SlotType.SERIAL && !reservation?.id && (
                        <Grid item xs={12} md={12}
                        >
                            <Typography sx={{ mb: 1 }}>{t('Multiday')}</Typography>
                            {defaultMultidayUntil && <Stack direction="row" gap={2} sx={{mb: 1}}>
                                <Stack direction="row" gap={0.5}
                                       alignItems="center">
                                    <CircleIcon sx={{
                                        fontSize: 12,
                                        color: CommonThemeColors.sys.success.light
                                    }}/>
                                    <Typography variant="caption"
                                                color="text.secondary">
                                        {t('Check in')}{checkInDate ? `: ${formatDateDMYHM(checkInDate)}` : ''}
                                    </Typography>
                                </Stack>
                                <Stack direction="row" gap={0.5}
                                       alignItems="center">
                                    <CircleIcon sx={{
                                        fontSize: 12,
                                        color: CommonThemeColors.sys.error.light
                                    }}/>
                                    <Typography variant="caption"
                                                color="text.secondary">
                                        {t('Check out')}{checkOutDate ? `: ${formatDateDMYHM(checkOutDate)}` : ''}
                                    </Typography>
                                </Stack>
                            </Stack>}
                            <Stack gap={2} direction="row">
                                <CDatePicker
                                    value={tmpMultidayUntil}
                                    dateOnly
                                    onChange={e => setTmpMultidayUntil(e)}
                                    onPageLoaded={(args: MbscDatepickerPageLoadedEvent, inst: DatepickerBase) => {
                                        // Only navigate on first load to check-in date
                                        if (!hasInitiallyNavigated && checkInDate) {
                                            inst.navigate(checkInDate)
                                            setHasInitiallyNavigated(true)
                                        }
                                    }}
                                    highlightColors={[
                                        { date: checkInDate, highlight: CommonThemeColors.sys.success.light },
                                        { date: checkOutDate, highlight: CommonThemeColors.sys.error.light },
                                        ...(tmpMultidayUntil ? [{ date: tmpMultidayUntil, highlight: CommonThemeColors.sys.primary.main }] : [])
                                    ]}
                                    label={t('End Date')}
                                />

                            </Stack>
                        </Grid>
                    )
                }
            </>
        </ReservationDetailsWrapper>
    )

}
export default ReservationDetailsRestaurant;
